import { type JSX, type ReactNode, useState } from 'react';

import {
  ActivityCardBottomLeftFactory,
  ActivityCardDescriptionFactory,
  ActivityCardHeroFactory,
  ActivityCardImageFactory,
  ActivityCardMetaFactory,
  ActivityCardMetaIconFactory,
  ActivityCardMetaItemContentFactory,
  ActivityCardMetaItemDescriptionFactory,
  ActivityCardMetaItemFactory,
  ActivityCardMetaTextFactory,
  ActivityCardRootFactory,
  ActivityCardTitleFactory,
  ActivityCardTitleLinkFactory,
  ActivityCardTopRightFactory,
} from './activity-card.factory';

type Props = {
  layout?: 'grid' | 'list';
  type?: 'default' | 'highlight';
  spacing?: 'default' | 'compact';
  interactive?: boolean;
  heroSrc?: string | null;
  heroAlt?: string;
  heroFallback?: string;
  /** Content rendered in the top-right overlay slot (actions, stage badge, etc.) */
  topRightSlot?: ReactNode;
  /** Content rendered in the bottom-left overlay slot (category tags, activity badges, etc.) */
  bottomLeftSlot?: ReactNode;
  title: ReactNode;
  /** Short excerpt — only rendered in list layout */
  description?: ReactNode;
  /** Meta rows and any card-specific content below the title */
  children?: ReactNode;
};

export function ActivityCard({
  layout = 'grid',
  type = 'default',
  spacing = 'default',
  interactive = false,
  heroSrc,
  heroAlt,
  heroFallback,
  topRightSlot,
  bottomLeftSlot,
  title,
  description,
  children,
}: Props): JSX.Element {
  const [imageFailed, setImageFailed] = useState(false);
  const imageSrc = !imageFailed && heroSrc ? heroSrc : heroFallback;

  return (
    <ActivityCardRootFactory
      layout={layout}
      type={type}
      spacing={spacing}
      interactive={interactive}>
      <ActivityCardHeroFactory
        layout={layout}
        type={type}
        spacing={spacing}>
        {imageSrc && (
          <ActivityCardImageFactory
            key={imageSrc}
            src={imageSrc}
            alt={heroAlt}
            onError={() => setImageFailed(true)}
          />
        )}
      </ActivityCardHeroFactory>

      {topRightSlot !== undefined && (
        <ActivityCardTopRightFactory layout={layout}>{topRightSlot}</ActivityCardTopRightFactory>
      )}

      {bottomLeftSlot !== undefined && (
        <ActivityCardBottomLeftFactory
          layout={layout}
          type={type}
          spacing={spacing}>
          {bottomLeftSlot}
        </ActivityCardBottomLeftFactory>
      )}

      <ActivityCardTitleFactory
        layout={layout}
        type={type}
        spacing={spacing}>
        {title}
      </ActivityCardTitleFactory>

      {layout === 'list' && description !== undefined && (
        <ActivityCardDescriptionFactory>{description}</ActivityCardDescriptionFactory>
      )}

      {children}
    </ActivityCardRootFactory>
  );
}

// Sub-components — used inside the `children` slot for meta rows and card-specific content
ActivityCard.Meta = ActivityCardMetaFactory;
ActivityCard.MetaItem = ActivityCardMetaItemFactory;
ActivityCard.MetaItemContent = ActivityCardMetaItemContentFactory;
ActivityCard.MetaItemDescription = ActivityCardMetaItemDescriptionFactory;
ActivityCard.MetaIcon = ActivityCardMetaIconFactory;
ActivityCard.MetaText = ActivityCardMetaTextFactory;
ActivityCard.TitleLink = ActivityCardTitleLinkFactory;
